import React from 'react';
import { Link, useHistory } from 'react-router-dom';
import { AdornedInput, Box, Button, Spacer, Tabs, Text } from '@zillow/constellation-10';
import { IconCloseOutline, IconEditFilled } from '@zillow/constellation-icons';
import type { RentalManagerUserCompanyStatsDto } from '@zg-rentals/rental-manager-env';
import { useRentalManagerUser, useRmwPaths } from '@zg-rentals/rental-manager-env';
import { UserManagementCompanyDetailsToolsApiHandler_renameCompany } from '@zg-rentals/ts-user-management-client';
import { useMutation } from '@zg-rentals/react-query5';
import { COMPANY_STATS_TAG } from '../UsersPage';
import { buttonEditStyles, styledRequestNotificationSpanStyles, styledTabCountSpanStyles } from './StyledComponents';
import { useApplicationPermissions } from '../../hooks/useApplicationPermissions';
import { emitAddNewUserEvent, EVENT_IDS } from '../../modal/ClickstreamEvents';

const UserManagementNav = ({
  company,
  activeTab,
}: {
  company: RentalManagerUserCompanyStatsDto | undefined;
  activeTab: string;
}) => {
  const paths = useRmwPaths();
  const [isEditing, setIsEditing] = React.useState(false);
  const [companyName, setCompanyName] = React.useState('');
  const [editError, setEditError] = React.useState(null) as [unknown, (error: unknown) => void];
  const { pendingApplicationRequestUsers } = useApplicationPermissions();
  const { user } = useRentalManagerUser();
  const history = useHistory();
  const canAccessUserManagementApplicationPermission = user.features.canAccessUserManagementApplicationPermission;

  const renameMutation = useMutation({
    mutationFn: (newCompanyName: string) =>
      UserManagementCompanyDetailsToolsApiHandler_renameCompany({
        newCompanyName,
        umcId: user.userManagementCompanyId,
      }),
    meta: {
      invalidates: [COMPANY_STATS_TAG],
      context: { api: 'user-management', details: 'renameCompany' },
    },
    onSuccess: (response) => {
      if (!response.isSuccess) {
        setEditError(response.errorMessage || 'Failed to update Company Name, please try again');
        return;
      }
      setIsEditing(false);
    },
    onError: (e: Error) => {
      setEditError(e.message || 'Failed to update Company Name, please try again');
    },
  });

  const loadTab = (tab: string) => {
    const baseDetailsUrl = paths.userManagementPage;
    const url = `${baseDetailsUrl}/${tab}`;
    return history.push(url);
  };

  const handleEdit = () => {
    if (!company?.nameUpdateAllowed) return;

    setEditError(null);
    setCompanyName(company.name);
    setIsEditing(true);
  };

  const handleSaveName = () => {
    if (!company?.nameUpdateAllowed) return;
    renameMutation.mutate(companyName);
  };

  const renderEditName = () => (
    <div className="edit-company-name-wrapper">
      <AdornedInput
        input={
          <AdornedInput.Input
            type="text"
            value={companyName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyName(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => e.keyCode === 13 && handleSaveName()}
            onBlur={handleSaveName}
            error={!!editError}
          />
        }
        endAdornment={
          <AdornedInput.Adornment>
            <IconCloseOutline className="close-edit-company-name-icon" onClick={() => setIsEditing(false)} />
          </AdornedInput.Adornment>
        }
      />
    </div>
  );

  const renderHeading = () =>
    company?.name && (
      <Box css={{ display: 'flex', alignItems: 'center' }}>
        <Text textStyle="heading-sm">{company.name}</Text>
        <Button.Icon
          title="Edit"
          aria-description="A pencil"
          onClick={handleEdit}
          size="md"
          aria-hidden="false"
          role="button"
          className={buttonEditStyles({ disabled: !company.nameUpdateAllowed })}
        >
          <IconEditFilled />
        </Button.Icon>
      </Box>
    );

  const TabList = {
    users: {
      id: 'users',
      path: paths.userManagementUsers,
    },
    pending: {
      id: 'pending',
      path: paths.userManagementPending,
    },
    applicationPermissions: {
      id: 'application-permissions',
      path: paths.userManagementApplicationPermissions,
    },
    activity: {
      id: 'activity',
      path: paths.userManagementActivity,
    },
    unrecognized: {
      id: 'unrecognized-users',
      path: paths.userManagementUnrecognizedUsers,
    },
  } as { [key: string]: { id: string; path: string } };

  const onTabSelected = (selected: string) => {
    const selectedTab = Object.keys(TabList).find((tab) => TabList[tab].id === selected);
    if (selectedTab) {
      loadTab(TabList[selectedTab].path);
    }
  };

  return (
    <div>
      <Spacer css={{ marginBlock: 'xs' }}>{isEditing ? renderEditName() : renderHeading()}</Spacer>
      <Box css={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Tabs.Root defaultSelected={activeTab} onSelectedChange={onTabSelected}>
          <Tabs.List>
            <Tabs.Tab css={{ paddingBlock: 400 }} value={TabList.users.id}>
              Active Users
              {!!company?.userCount && (
                <Text asChild>
                  <span className={styledTabCountSpanStyles}>{company.userCount}</span>
                </Text>
              )}
            </Tabs.Tab>
            <Tabs.Tab css={{ paddingBlock: 400 }} value={TabList.unrecognized.id}>
              Unrecognized Users
            </Tabs.Tab>
            <Tabs.Tab css={{ paddingBlock: 400 }} value={TabList.pending.id}>
              Pending Users
              {!!company?.pendingUserCount && (
                <Text asChild>
                  <span className={styledTabCountSpanStyles}>{company.pendingUserCount}</span>
                </Text>
              )}
            </Tabs.Tab>
            {canAccessUserManagementApplicationPermission && (
              <Tabs.Tab css={{ paddingBlock: 400 }} value={TabList.applicationPermissions.id}>
                Application Permissions
                {!!pendingApplicationRequestUsers.length && (
                  <Text asChild>
                    <span className={styledRequestNotificationSpanStyles}>{pendingApplicationRequestUsers.length}</span>
                  </Text>
                )}
              </Tabs.Tab>
            )}
            <Tabs.Tab value={TabList.activity.id}>Activity History</Tabs.Tab>
          </Tabs.List>
        </Tabs.Root>
        <Box css={{ marginLeft: 'auto', flexShrink: 0 }}>
          <Button
            tone="brand"
            emphasis="filled"
            asChild
            onClick={() => emitAddNewUserEvent({ user })}
            data-eventid={EVENT_IDS.ADD_NEW_USER}
          >
            <Link to={`${paths.userManagementPage}/${paths.userManagementAddUser}`}>Add a new user</Link>
          </Button>
        </Box>
      </Box>
    </div>
  );
};

export default UserManagementNav;
